/**
 * ArPage 應收帳款 (S8.1) — Board D #8, 會計 Finance nav.
 *
 * Split out of the codex `FinancePages.tsx` onto the house list stack
 * (useListPage + GenericFilterPopover + TableContainer state triad).
 *
 * - Rows come FROM the customer invoices (S7.1 invoice-on-loaded records —
 *   one record, many lenses); this page is the collection lens: 未收/已收
 *   plus overdue derived off the payment-terms due date (`collection` block
 *   is server-computed by the same handler the invoice pages read).
 * - 記錄收款 advances 未收 → 部分已收/已收, bounded by the outstanding
 *   amount; 作廢 void requires a mandatory reason (ReasonRequiredModal).
 * - Actions are HIDDEN when not applicable (void/collected rows lose 記錄收款,
 *   collected/void rows lose 作廢) — hide-don't-disable.
 *
 * Slice-local seam (S8.1 file-set): api calls + row types are co-located here
 * because `src/hooks/**` / `src/services/api/**` belong to concurrent slices —
 * follow-up: lift into `useArInvoices.ts` when those files free up.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Badge,
  Button,
  Col,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Row,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  FilterOutlined,
  MoreOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAntdTable } from 'ahooks';
import PageHeader from '@/components/common/PageHeader';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import { extractApiError } from '@/hooks/useSalesOrders';
import apiClient from '@/services/api/axios.config';
import { clearCacheWhere } from '@/utils/cacheKeyRegistry';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type { CustomerInvoice } from '@/types/api/finance';
import { formatMoney } from '../salesOrders/components/soFormat';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Slice-local data seam (mirrors the handler response shapes — see docblock)
// ---------------------------------------------------------------------------

/** Collection lens block — server-derived off payment terms (S8.1). */
interface ArCollection {
  status: 'uncollected' | 'partial' | 'collected' | 'void';
  outstandingAmount: number;
  overdue: boolean;
}

type ArRow = CustomerInvoice & { collection: ArCollection };

const arApi = {
  list: async (): Promise<{ data: ArRow[] }> => {
    const { data } = await apiClient.get<{ data: ArRow[] }>('/finance/ar-invoices');
    return data;
  },
  receive: async (id: string, amount: number): Promise<{ data: ArRow }> => {
    const { data } = await apiClient.post<{ data: ArRow }>(`/finance/ar-invoices/${id}/receive`, {
      amount,
    });
    return data;
  },
  void: async (id: string, reason: string): Promise<{ data: ArRow }> => {
    const { data } = await apiClient.post<{ data: ArRow }>(`/finance/ar-invoices/${id}/void`, {
      reason,
    });
    return data;
  },
};

/** AR writes stale the invoice lenses too (same record, many lenses). */
function invalidateArCaches() {
  clearCacheWhere((key) => key.startsWith('/api/finance') || key.startsWith('/api/occ'));
}

type CollectionFilter = 'all' | 'uncollected' | 'partial' | 'collected' | 'overdue' | 'void';

const COLLECTION_FILTERS: readonly Exclude<CollectionFilter, 'all'>[] = [
  'uncollected',
  'partial',
  'collected',
  'overdue',
  'void',
] as const;

const COLLECTION_COLOR: Record<ArCollection['status'], string> = {
  uncollected: 'orange',
  partial: 'blue',
  collected: 'green',
  void: 'default',
};

interface ArFilters {
  search: string;
  collection: CollectionFilter;
  [key: string]: unknown;
}

const defaultArFilters = (): ArFilters => ({ search: '', collection: 'all' });

function matchesCollectionFilter(row: ArRow, filter: CollectionFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'overdue') return row.collection.overdue;
  return row.collection.status === filter;
}

function useArInvoicesTable(filters: ArFilters): TableHookResult<ArRow> {
  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      const rows = (await arApi.list()).data;
      const term = filters.search.trim().toLowerCase();
      const filtered = rows.filter((row) => {
        if (!matchesCollectionFilter(row, filters.collection)) return false;
        if (!term) return true;
        return [row.invoiceNumber, row.customerName, row.soNumber ?? '']
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
      const start = (current - 1) * pageSize;
      return { total: filtered.length, list: filtered.slice(start, start + pageSize) };
    },
    { defaultPageSize: 10, refreshDeps: [filters.search, filters.collection] }
  );
  return { tableProps, isLoading: loading || params.length === 0, error: error ?? null, refresh };
}

const receiveSchema = z.object({
  amount: z
    .number({ message: 'finance:ar.receiveModal.validation.amountRequired' })
    .positive({ message: 'finance:ar.receiveModal.validation.amountRequired' }),
});

type ReceiveFormData = z.infer<typeof receiveSchema>;

interface ArFilterPopoverValues extends FilterValues {
  collection?: string;
}

const ArPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['finance', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const [filters, setFilters] = useState<ArFilters>(defaultArFilters);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [receiveRow, setReceiveRow] = useState<ArRow | null>(null);
  const [voidRow, setVoidRow] = useState<ArRow | null>(null);

  const customFilterCounters = useMemo(
    () => ({ collection: (value: unknown) => (value === 'all' ? 0 : 1) }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    ArFilters,
    ArRow
  >({
    useTableHook: useArInvoicesTable,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReceiveFormData>({
    resolver: zodResolver(receiveSchema),
    defaultValues: { amount: 0 },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const openReceive = (row: ArRow) => {
    setReceiveRow(row);
    reset({ amount: row.collection.outstandingAmount });
  };

  const closeReceive = () => {
    setReceiveRow(null);
    reset({ amount: 0 });
  };

  const onReceive = async (values: ReceiveFormData) => {
    if (!receiveRow) return;
    try {
      await arApi.receive(receiveRow.id, values.amount);
      invalidateArCaches();
      message.success(t('finance:ar.messages.received'));
      closeReceive();
      refresh();
    } catch (error) {
      const body = extractApiError(error);
      message.error(
        body.error === 'INVALID_AMOUNT'
          ? t('finance:ar.receiveModal.validation.amountTooHigh')
          : body.message || t('common:messages.updateFailed')
      );
    }
  };

  const onVoid = async (reason: string) => {
    if (!voidRow) return;
    try {
      await arApi.void(voidRow.id, reason);
      invalidateArCaches();
      message.success(t('finance:ar.messages.voided'));
      setVoidRow(null);
      refresh();
    } catch (error) {
      message.error(extractApiError(error).message || t('common:messages.updateFailed'));
    }
  };

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'collection',
        label: t('finance:ar.fields.collection'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('finance:ar.filters.all') },
          ...COLLECTION_FILTERS.map((value) => ({
            value,
            label: t(`finance:ar.collection.${value}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t]
  );

  const columns: TableProps<ArRow>['columns'] = [
    {
      title: t('finance:ar.fields.invoiceNumber'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 170,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.invoiceNumber} searchTerm={filters.search} fallback="-" />
          </span>
          {record.issuedAt && (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {record.issuedAt.slice(0, 10)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('finance:ar.fields.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 240,
      render: (value: string) => (
        <HighlightText text={value} searchTerm={filters.search} fallback="-" />
      ),
    },
    {
      title: t('finance:ar.fields.salesOrder'),
      dataIndex: 'soNumber',
      key: 'soNumber',
      width: 160,
      render: (value: string | null) =>
        value ? <HighlightText text={value} searchTerm={filters.search} fallback="-" /> : renderDash(),
    },
    {
      title: t('finance:ar.fields.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (_: number, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(record.currency, record.amount)}
        </span>
      ),
    },
    {
      title: t('finance:ar.fields.paidAmount'),
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      width: 140,
      align: 'right',
      render: (_: number, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(record.currency, record.paidAmount)}
        </span>
      ),
    },
    {
      title: t('finance:ar.fields.outstanding'),
      key: 'outstanding',
      width: 140,
      align: 'right',
      render: (_: unknown, record) => (
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: token.fontWeightStrong,
            color:
              record.collection.outstandingAmount > 0 ? token.colorPrimary : token.colorTextSecondary,
          }}
        >
          {formatMoney(record.currency, record.collection.outstandingAmount)}
        </span>
      ),
    },
    {
      // Overdue derives off the payment-terms due date (server-computed).
      title: t('finance:ar.fields.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 140,
      render: (value: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ color: record.collection.overdue ? token.colorError : undefined }}>
            {value || renderDash()}
          </span>
          {record.collection.overdue && (
            <Tag color="red" style={{ margin: 0, width: 'fit-content' }}>
              {t('finance:ar.collection.overdue')}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: t('finance:ar.fields.collection'),
      key: 'collection',
      width: 120,
      render: (_: unknown, record) => (
        <Tag color={COLLECTION_COLOR[record.collection.status]} style={{ margin: 0 }}>
          {t(`finance:ar.collection.${record.collection.status}`)}
        </Tag>
      ),
    },
    {
      title: t('finance:ar.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => {
        const collectible =
          record.collection.status === 'uncollected' || record.collection.status === 'partial';
        return (
          <Dropdown
            menu={{
              items: [
                // 記錄收款 hidden when void/collected (hide-don't-disable)
                ...(collectible
                  ? [
                      {
                        key: 'receive',
                        label: t('finance:ar.actions.receive'),
                        icon: <DollarOutlined />,
                        onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                          e.domEvent.stopPropagation();
                          openReceive(record);
                        },
                      },
                    ]
                  : []),
                {
                  key: 'view',
                  label: t('finance:ar.actions.view'),
                  icon: <FileTextOutlined />,
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    navigate(`/auth/finance/invoices/customer/${record.id}`);
                  },
                },
                ...(collectible
                  ? [
                      {
                        key: 'void',
                        label: t('finance:ar.actions.void'),
                        icon: <StopOutlined />,
                        danger: true,
                        onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                          e.domEvent.stopPropagation();
                          setVoidRow(record);
                        },
                      },
                    ]
                  : []),
              ],
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              aria-label={t('finance:ar.actions.actionsFor', {
                invoiceNumber: record.invoiceNumber,
              })}
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('finance:ar.title')} />

      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('finance:ar.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<ArFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={(values) =>
            setFilters((prev) => ({
              ...prev,
              collection: (values.collection as CollectionFilter) || 'all',
            }))
          }
          initialFilters={{ collection: filters.collection }}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
      </div>

      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('finance:ar.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ArRow>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => navigate(`/auth/finance/invoices/customer/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('finance:ar.states.noResults')}
                actionLabel={t('finance:ar.states.clearFilters')}
                onAction={() => setFilters(defaultArFilters())}
                actionType="default"
              />
            ) : (
              <ListEmptyState title={t('finance:ar.states.noInvoices')} />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1330 }}
        />
      </TableContainer>

      {/* 記錄收款 — bounded by the outstanding amount */}
      <Modal
        open={!!receiveRow}
        title={t('finance:ar.receiveModal.title')}
        onCancel={closeReceive}
        width={520}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeReceive}
          >
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="receive"
            type="primary"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            loading={isSubmitting}
            onClick={() => void handleSubmit(onReceive)()}
          >
            {t('finance:ar.receiveModal.submit')}
          </Button>,
        ]}
      >
        {receiveRow && (
          <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
            {t('finance:ar.receiveModal.outstandingLabel')}
            {': '}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(receiveRow.currency, receiveRow.collection.outstandingAmount)}
            </span>
          </Text>
        )}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('finance:ar.receiveModal.amountLabel')}
              {receiveRow ? ` (${receiveRow.currency})` : ''}
            </Text>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <InputNumber
                  value={field.value}
                  onChange={(value) => field.onChange(value ?? 0)}
                  onBlur={field.onBlur}
                  min={0}
                  max={receiveRow?.collection.outstandingAmount}
                  size="large"
                  style={{ ...formStyles.inputStyle, width: '100%' }}
                  status={errors.amount ? 'error' : undefined}
                />
              )}
            />
            {errors.amount && (
              <Text style={formStyles.errorStyle}>{t(errors.amount.message ?? '')}</Text>
            )}
          </Col>
        </Row>
      </Modal>

      {/* 作廢 — mandatory reason (audit-logged) */}
      <ReasonRequiredModal
        open={!!voidRow}
        title={t('finance:ar.voidModal.title')}
        submitText={t('finance:ar.voidModal.submit')}
        cancelText={t('common:actions.cancel')}
        reasonLabel={t('finance:ar.voidModal.reasonLabel')}
        requiredMessage={t('finance:ar.voidModal.reasonRequired')}
        description={t('finance:ar.voidModal.description')}
        onSubmit={onVoid}
        onCancel={() => setVoidRow(null)}
      />
    </div>
  );
};

export default ArPage;
